import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { api, formatRp } from "@/lib/api";
import { CheckCircle2, XCircle, Clock, Calendar, Zap } from "lucide-react";

export default function WithdrawalManager({ role }) {
  const [sellerWD, setSellerWD] = useState([]);
  const [salesWD, setSalesWD] = useState([]);
  const [running, setRunning] = useState(false);

  const load = async () => {
    try {
      const [s, sf] = await Promise.all([
        api.get("/seller/withdrawals"),
        api.get("/sales/withdrawals"),
      ]);
      setSellerWD(s.data);
      setSalesWD(sf.data);
    } catch { toast.error("Gagal load"); }
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    try { await api.post(`/seller/withdrawals/${id}/approve`); toast.success("Approved"); load(); }
    catch { toast.error("Gagal"); }
  };
  const reject = async (id) => {
    try { await api.post(`/seller/withdrawals/${id}/reject`); toast.success("Rejected"); load(); }
    catch { toast.error("Gagal"); }
  };
  const runMonthly = async () => {
    setRunning(true);
    try {
      const r = await api.post("/sales/withdrawals/run");
      toast.success(`Payout ${r.data.payouts} sales · Total ${formatRp(r.data.total)}`);
      load();
    } catch { toast.error("Gagal run"); }
    setRunning(false);
  };

  return (
    <Tabs defaultValue="seller" className="w-full">
      <TabsList className="bg-slate-900/60 border border-slate-800 mb-4">
        <TabsTrigger value="seller" data-testid="wm-tab-seller">Withdrawal Seller</TabsTrigger>
        <TabsTrigger value="sales" data-testid="wm-tab-sales">Withdrawal Sales Freelance</TabsTrigger>
      </TabsList>

      <TabsContent value="seller">
        <Card className="glass glow-border bg-transparent rounded-2xl p-6">
          <h3 className="font-display font-bold text-white text-lg mb-4">Permintaan Withdrawal Seller</h3>
          <div className="space-y-2">
            {sellerWD.length === 0 && <div className="text-slate-500 text-sm">Belum ada permintaan.</div>}
            {sellerWD.map(w => (
              <div key={w.requested_at} data-testid={`swd-${w.seller_email}`} className="bg-slate-900/40 border border-slate-800 rounded-lg p-4 flex justify-between items-center flex-wrap gap-3">
                <div>
                  <div className="text-white font-semibold">{w.seller_email}</div>
                  <div className="text-xs text-slate-400">{w.bank_name} · {w.bank_account}</div>
                  <div className="text-[10px] text-slate-500 font-mono-metric mt-0.5">{new Date(w.requested_at).toLocaleString("id-ID")}</div>
                </div>
                <div className="font-mono-metric text-cyan-300 font-bold text-lg">{formatRp(w.amount)}</div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-1 rounded font-mono-metric ${w.status === "paid" ? "bg-emerald-500/20 text-emerald-300" : w.status === "rejected" ? "bg-red-500/20 text-red-300" : "bg-amber-500/20 text-amber-300"}`}>
                    {w.status === "paid" ? <CheckCircle2 className="w-3 h-3 inline mr-1"/> : w.status === "rejected" ? <XCircle className="w-3 h-3 inline mr-1"/> : <Clock className="w-3 h-3 inline mr-1"/>}
                    {w.status.toUpperCase()}
                  </span>
                  {w.status === "pending" && (role === "admin" || role === "finance") && (
                    <>
                      <Button size="sm" onClick={() => approve(w._id || w.id)} className="h-7 bg-emerald-400 hover:bg-emerald-300 text-slate-950 font-bold text-xs">Approve</Button>
                      <Button size="sm" variant="outline" onClick={() => reject(w._id || w.id)} className="h-7 border-red-500/40 text-red-300 text-xs">Reject</Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </TabsContent>

      <TabsContent value="sales">
        <Card className="glass glow-border bg-transparent rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <h3 className="font-display font-bold text-white text-lg">Payout Sales Freelance</h3>
              <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5"><Calendar className="w-3 h-3"/> Otomatis setiap tanggal 28 tiap bulan · Fee 0,5% dari transaksi referral</p>
            </div>
            {(role === "admin" || role === "finance") && (
              <Button data-testid="run-monthly-btn" onClick={runMonthly} disabled={running} className="bg-amber-400 hover:bg-amber-300 text-slate-950 font-bold"><Zap className="w-4 h-4 mr-1"/>{running ? "Memproses..." : "Jalankan Payout Sekarang"}</Button>
            )}
          </div>
          <div className="space-y-2">
            {salesWD.length === 0 && <div className="text-slate-500 text-sm">Belum ada payout.</div>}
            {salesWD.map(w => (
              <div key={w.paid_at + w.sales_code} data-testid={`sfwd-${w.sales_code}`} className="bg-slate-900/40 border border-slate-800 rounded-lg p-4 flex justify-between items-center flex-wrap gap-3">
                <div>
                  <div className="text-white font-semibold">{w.sales_name}</div>
                  <div className="text-xs text-slate-400 font-mono-metric">{w.sales_code} · {w.bank_name} {w.bank_account}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">Periode {w.period} · {w.count} transaksi</div>
                </div>
                <div className="font-mono-metric text-emerald-300 font-bold text-lg">{formatRp(w.amount)}</div>
                <span className="text-xs px-2 py-1 rounded font-mono-metric bg-emerald-500/20 text-emerald-300"><CheckCircle2 className="w-3 h-3 inline mr-1"/>PAID</span>
              </div>
            ))}
          </div>
        </Card>
      </TabsContent>
    </Tabs>
  );
}
